import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ImageIcon, 
  Loader2, 
  Download, 
  Wand2, 
  Upload, 
  Edit3, 
  X, 
  History, 
  Trash2, 
  Sparkles, 
  Share2, 
  Copy, 
  Check, 
  Plus,
  Send,
  Camera,
  RotateCcw,
  Maximize2
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { useLanguage } from "../context/LanguageContext";
import { ScrollArea } from "../components/ui/scroll-area";

// Send Button Logo Component
const SendButtonLogo = ({ size = 40, loading = false }) => (
  <div 
    className={cn(
      "rounded-xl bg-foreground flex items-center justify-center transition-all",
      loading && "animate-pulse"
    )}
    style={{ width: size, height: size }}
  >
    {loading ? (
      <Loader2 className="w-5 h-5 text-background animate-spin" />
    ) : (
      <Wand2 className="w-5 h-5 text-background" />
    )}
  </div>
);

// Image Message Component
const ImageMessage = ({ image, isUser, onEdit, onDownload, onDelete, onShare, isRTL }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    className={cn(
      "flex gap-3 max-w-[85%]",
      isUser ? "ms-auto flex-row-reverse" : "me-auto"
    )}
  >
    <div className={cn(
      "rounded-2xl overflow-hidden",
      isUser ? "bg-foreground/10" : "bg-secondary border border-border"
    )}>
      {/* Image */}
      <div className="relative group">
        <img 
          src={image.preview || `data:image/png;base64,${image.base64}`}
          alt={image.prompt || "Generated image"}
          className="w-full max-w-sm rounded-t-2xl object-cover"
        />
        
        {/* Hover Actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {onEdit && (
            <button
              onClick={() => onEdit(image)}
              className="p-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-colors"
              title={isRTL ? "تعديل" : "Edit"}
            >
              <Edit3 className="w-5 h-5" />
            </button>
          )}
          {onDownload && (
            <button
              onClick={() => onDownload(image)}
              className="p-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-colors"
              title={isRTL ? "تحميل" : "Download"}
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          {onShare && (
            <button
              onClick={() => onShare(image)}
              className="p-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-colors"
              title={isRTL ? "مشاركة" : "Share"}
            >
              <Share2 className="w-5 h-5" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(image)}
              className="p-2.5 rounded-xl bg-red-500/50 hover:bg-red-500/70 text-white transition-colors"
              title={isRTL ? "حذف" : "Delete"}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      
      {/* Prompt */}
      {image.prompt && (
        <div className="px-4 py-3">
          <p className="text-sm text-foreground leading-relaxed">{image.prompt}</p>
        </div>
      )}
    </div>
  </motion.div>
);

// Empty State
const EmptyState = ({ isRTL, onQuickAction }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex flex-col items-center justify-center h-full text-center px-6 pt-20"
  >
    <motion.div 
      className="w-20 h-20 rounded-3xl bg-secondary flex items-center justify-center mb-6"
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      <Sparkles className="w-10 h-10 text-foreground" />
    </motion.div>
    
    <h2 className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
      {isRTL ? "أنشئ صورة جديدة" : "Create a New Image"}
    </h2>
    
    <p className="text-muted-foreground max-w-md mb-8">
      {isRTL 
        ? "صف الصورة التي تريدها وسيقوم HELCOO بإنشائها لك باستخدام الذكاء الاصطناعي"
        : "Describe the image you want and HELCOO will create it using AI"}
    </p>

    {/* Quick suggestions */}
    <div className="flex flex-wrap gap-2 justify-center">
      {[
        isRTL ? "قطة لطيفة" : "Cute cat",
        isRTL ? "غروب الشمس" : "Sunset",
        isRTL ? "مدينة المستقبل" : "Future city",
        isRTL ? "طبيعة خلابة" : "Beautiful nature"
      ].map((suggestion, i) => (
        <motion.button
          key={i}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 + i * 0.1 }}
          onClick={() => onQuickAction(suggestion)}
          className="px-4 py-2 rounded-full bg-secondary text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
        >
          {suggestion}
        </motion.button>
      ))}
    </div>
  </motion.div>
);

export default function ImageGenPage() {
  const { t, isRTL } = useLanguage();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]); // Array of {type: 'prompt'|'image', content, timestamp}
  const [uploadedImages, setUploadedImages] = useState([]); // Array of images
  const [showGallery, setShowGallery] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchGallery = async () => {
    setLoadingGallery(true);
    try {
      const response = await api.get("/image/gallery");
      setGallery(response.data || []);
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setLoadingGallery(false);
    }
  };

  // Handle multiple file uploads
  const handleFileUpload = (e, source = 'gallery') => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(isRTL ? `${file.name} ليس صورة` : `${file.name} is not an image`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(isRTL ? `${file.name} كبير جداً` : `${file.name} is too large`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Process each file
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        const newImage = {
          id: Date.now() + Math.random(),
          base64,
          preview: event.target.result,
          name: file.name
        };
        setUploadedImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
    
    const count = validFiles.length;
    if (count === 1) {
      toast.success(isRTL ? "تم تحميل الصورة" : "Image loaded");
    } else {
      toast.success(isRTL ? `تم تحميل ${count} صور` : `${count} images loaded`);
    }
    
    // Reset input
    e.target.value = '';
  };

  // Remove a single image from uploaded images
  const removeUploadedImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleGenerate = async () => {
    if (!prompt.trim() && uploadedImages.length === 0) return;
    if (loading) return;

    const currentPrompt = prompt.trim();
    const currentImages = [...uploadedImages];
    
    // Add prompt to conversation
    if (currentPrompt || currentImages.length > 0) {
      setConversation(prev => [...prev, {
        type: 'prompt',
        content: currentPrompt || (isRTL ? "تعديل الصور" : "Edit images"),
        timestamp: new Date().toISOString(),
        imageCount: currentImages.length,
        images: currentImages.map(img => img.preview)
      }]);
    }

    setPrompt("");
    setUploadedImages([]);
    setLoading(true);

    try {
      let response;
      
      if (currentImages.length > 0) {
        // Edit mode - use first image for now
        response = await api.post("/image/edit", {
          prompt: currentPrompt || (isRTL ? "حسّن هذه الصورة" : "Enhance this image"),
          image_base64: currentImages[0].base64
        });
      } else {
        // Generate mode
        response = await api.post("/image/generate", {
          prompt: currentPrompt
        });
      }

      if (response.data.image_base64) {
        const newImage = {
          type: 'image',
          base64: response.data.image_base64,
          preview: `data:image/png;base64,${response.data.image_base64}`,
          prompt: currentPrompt,
          timestamp: new Date().toISOString(),
          isEdited: currentImages.length > 0
        };
        
        setConversation(prev => [...prev, newImage]);
        fetchGallery();
        
        toast.success(currentImages.length > 0 
          ? (isRTL ? "تم تعديل الصورة ✨" : "Image edited ✨") 
          : (isRTL ? "تم إنشاء الصورة ✨" : "Image created ✨"));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (isRTL ? "خطأ في توليد الصورة" : "Image generation error");
      toast.error(errorMsg);
      
      // Remove the prompt from conversation on error
      setConversation(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleQuickAction = (suggestion) => {
    setPrompt(suggestion);
    inputRef.current?.focus();
  };

  const handleEditImage = (image) => {
    setUploadedImages([{
      id: Date.now(),
      base64: image.base64,
      preview: image.preview,
      name: 'edit-image.png'
    }]);
    setPrompt(isRTL ? "عدّل هذه الصورة: " : "Edit this image: ");
    inputRef.current?.focus();
  };

  const handleDownloadImage = (image) => {
    const link = document.createElement('a');
    link.href = image.preview || `data:image/png;base64,${image.base64}`;
    link.download = `helcoo-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(isRTL ? "جاري تحميل الصورة..." : "Downloading image...");
  };

  const handleDeleteImage = async (image) => {
    if (image.id) {
      try {
        await api.delete(`/image/gallery/${image.id}`);
        setGallery(prev => prev.filter(img => img.id !== image.id));
        toast.success(isRTL ? "تم حذف الصورة" : "Image deleted");
      } catch (error) {
        toast.error(isRTL ? "خطأ في الحذف" : "Delete error");
      }
    }
  };

  const clearConversation = () => {
    setConversation([]);
    setUploadedImages([]);
    setPrompt("");
  };

  const selectGalleryImage = (image) => {
    const newImage = {
      id: Date.now(),
      base64: image.full_image || image.image_base64,
      preview: `data:image/png;base64,${image.full_image || image.image_base64}`,
      name: `gallery-${image.id}.png`
    };
    setUploadedImages(prev => [...prev, newImage]);
    setShowGallery(false);
    toast.success(isRTL ? "تم إضافة الصورة" : "Image added");
  };

  return (
    <div className="h-screen bg-background flex flex-col" data-testid="image-gen-page">
      {/* Mobile Header Spacer */}
      <div className="md:hidden h-16 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0)' }} />

      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-foreground flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-background" />
            </div>
            <div>
              <h1 className="text-base font-bold text-foreground">{isRTL ? "توليد الصور" : "Image Generation"}</h1>
              <p className="text-[10px] text-muted-foreground">HELCOO AI</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {conversation.length > 0 && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={clearConversation}
                className="p-2.5 rounded-xl bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                title={isRTL ? "محادثة جديدة" : "New conversation"}
              >
                <RotateCcw className="w-4 h-4" />
              </motion.button>
            )}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => { setShowGallery(true); fetchGallery(); }}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-secondary text-foreground text-sm font-medium"
            >
              <History className="w-4 h-4" />
              <span>{isRTL ? "المعرض" : "Gallery"}</span>
              {gallery.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-md bg-foreground text-background text-xs">
                  {gallery.length}
                </span>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Conversation Area */}
      <ScrollArea className="flex-1 overflow-auto">
        <div className="w-full max-w-3xl mx-auto px-4 py-6" style={{ paddingBottom: '120px' }}>
          {conversation.length === 0 ? (
            <EmptyState isRTL={isRTL} onQuickAction={handleQuickAction} />
          ) : (
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {conversation.map((item, index) => (
                  <div key={index}>
                    {item.type === 'prompt' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-end mb-2"
                      >
                        <div className="bg-foreground text-background px-4 py-3 rounded-2xl rounded-br-md max-w-[80%]">
                          <p className="text-sm">{item.content}</p>
                          {item.imageCount > 0 && (
                            <div className="mt-2">
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.images?.slice(0, 4).map((imgSrc, i) => (
                                  <img 
                                    key={i} 
                                    src={imgSrc} 
                                    alt={`Attached ${i + 1}`}
                                    className="w-10 h-10 rounded-lg object-cover"
                                  />
                                ))}
                                {item.imageCount > 4 && (
                                  <span className="w-10 h-10 rounded-lg bg-background/20 flex items-center justify-center text-xs">
                                    +{item.imageCount - 4}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs opacity-70 mt-1 flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                {item.imageCount} {isRTL ? "صورة" : "image(s)"}
                              </span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                    
                    {item.type === 'image' && (
                      <ImageMessage
                        image={item}
                        isUser={false}
                        onEdit={handleEditImage}
                        onDownload={handleDownloadImage}
                        isRTL={isRTL}
                      />
                    )}
                  </div>
                ))}
              </AnimatePresence>
              
              {/* Loading */}
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 text-muted-foreground"
                >
                  <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                  <span className="text-sm">{isRTL ? "جاري إنشاء الصورة..." : "Creating image..."}</span>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border"
        style={{ 
          paddingBottom: isMobile ? 'calc(70px + env(safe-area-inset-bottom, 0))' : '16px',
          paddingTop: '12px',
          paddingLeft: '16px',
          paddingRight: '16px'
        }}
      >
        {/* Multiple Images Preview */}
        <AnimatePresence>
          {uploadedImages.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mb-3 flex flex-wrap gap-2"
            >
              {uploadedImages.map((img, index) => (
                <div key={img.id} className="relative">
                  <img 
                    src={img.preview} 
                    alt={`Preview ${index + 1}`} 
                    className="h-16 w-16 rounded-xl border border-border object-cover"
                  />
                  <button
                    onClick={() => removeUploadedImage(img.id)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <span className="self-end text-xs text-muted-foreground mb-1">
                {uploadedImages.length} {isRTL ? "صورة" : "image(s)"}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hidden Input - Direct Native Picker */}
        <input
          ref={fileInputRef}
          type="file"
          id="image-upload"
          className="hidden"
          accept="image/*"
          multiple
          onChange={(e) => handleFileUpload(e, 'gallery')}
        />

        {/* Input Box */}
        <motion.div 
          className="flex items-center gap-2 p-2 rounded-2xl bg-secondary/50 border border-border/60"
          style={{ flexDirection: 'row-reverse' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Send Button */}
          <motion.button
            type="button"
            disabled={(!prompt.trim() && uploadedImages.length === 0) || loading}
            onClick={handleGenerate}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0 disabled:opacity-40"
            data-testid="generate-btn"
          >
            <SendButtonLogo size={42} loading={loading} />
          </motion.button>
          
          {/* Input Field */}
          <input
            ref={inputRef}
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={uploadedImages.length > 0 
              ? (isRTL ? "صف التعديل المطلوب..." : "Describe the edit...") 
              : (isRTL ? "صف الصورة التي تريدها..." : "Describe the image you want...")}
            className="flex-1 bg-transparent border-none outline-none px-3 py-2.5 text-foreground text-sm min-w-0"
            style={{ textAlign: isRTL ? 'right' : 'left' }}
            data-testid="prompt-input"
            dir="auto"
            autoComplete="off"
          />

          {/* Attach Button - Opens Native File Picker Directly */}
          <label
            htmlFor="image-upload"
            className={cn(
              "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors cursor-pointer",
              "bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground"
            )}
            data-testid="attach-btn"
          >
            <Plus className="w-5 h-5" />
          </label>
        </motion.div>
      </div>

      {/* Gallery Modal */}
      <AnimatePresence>
        {showGallery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowGallery(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 350 }}
              className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gallery Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="text-lg font-bold">{isRTL ? "معرض الصور" : "Image Gallery"}</h3>
                <button
                  onClick={() => setShowGallery(false)}
                  className="p-2 rounded-xl bg-secondary text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Gallery Content */}
              <ScrollArea className="h-[60vh] p-4">
                {loadingGallery ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : gallery.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <ImageIcon className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{isRTL ? "لا توجد صور بعد" : "No images yet"}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {gallery.map((image) => (
                      <motion.div
                        key={image.id}
                        whileHover={{ scale: 1.02 }}
                        className="relative group rounded-xl overflow-hidden border border-border cursor-pointer"
                        onClick={() => selectGalleryImage(image)}
                      >
                        <img
                          src={`data:image/png;base64,${image.thumbnail || image.image_base64}`}
                          alt={image.prompt}
                          className="w-full aspect-square object-cover"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {isRTL ? "استخدام" : "Use"}
                          </span>
                        </div>
                        
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(image);
                          }}
                          className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
